import { useEffect } from "react";

/**
 * Lightweight SEO component — updates <title>, <meta description>, <meta keywords>,
 * canonical URL, Open Graph + Twitter Card tags, and <html lang> on mount.
 *
 * Usage:
 *   <Seo title="..." description="..." path="/campagnes" image="..." lang="fr" type="website" />
 *
 * All props are optional except title.
 */
const ensureMeta = (selector, attr, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [tag, ...rest] = selector.replace(/[\[\]"]/g, "").split(/[=]/);
    if (tag === "meta") {
      // selector like meta[name=description] or meta[property=og:title]
      const [, k, v] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
      if (k && v) el.setAttribute(k, v);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setMetaByName = (name, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setMetaByProperty = (prop, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (url) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
};

const SITE_URL = "https://bonheursolidaire.com";
const SITE_NAME = "Fondation Cœur Solidaire";
const DEFAULT_IMAGE = `${SITE_URL}/og-cover.jpg`;

const Seo = ({
  title,
  description,
  path = "",
  image,
  lang = "fr",
  type = "website",
  noindex = false,
}) => {
  useEffect(() => {
    const fullTitle = title?.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`;
    const url = `${SITE_URL}${path || "/"}`;
    const img = image || DEFAULT_IMAGE;

    document.title = fullTitle;
    document.documentElement.setAttribute("lang", lang);
    setMetaByName("description", description);
    setMetaByName("robots", noindex ? "noindex, nofollow" : "index, follow");
    setCanonical(url);

    // Open Graph
    setMetaByProperty("og:type", type);
    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:title", fullTitle);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", url);
    setMetaByProperty("og:image", img);
    setMetaByProperty("og:locale", lang === "fr" ? "fr_FR" : "en_US");

    // Twitter
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", fullTitle);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", img);
  }, [title, description, path, image, lang, type, noindex]);

  return null;
};

export default Seo;
